import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../helpers/api";
import { ShowToast } from "../../components/toast";

export type ChargeAuthMe = {
  authorized: boolean;
  enrolled: boolean;
  isManager: boolean;
};

export type ChargeAuthRow = {
  id: string;
  email: string;
  isManager: boolean;
  enrolledAt: string | null;
};

export const useChargeAuthMe = () =>
  useQuery<ChargeAuthMe>({
    queryKey: ["charge-auth-me"],
    queryFn: () => api.chargeAuth.me(),
    refetchOnWindowFocus: false,
  });

export const useChargeAuthList = (enabled: boolean) =>
  useQuery<ChargeAuthRow[]>({
    queryKey: ["charge-auth-list"],
    queryFn: () => api.chargeAuth.list(),
    enabled,
  });

const invalidate = (qc: ReturnType<typeof useQueryClient>) =>
  qc.invalidateQueries({ queryKey: ["charge-auth-list"] });

export const useAddChargeAuth = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { email: string; isManager?: boolean }) =>
      api.chargeAuth.add(data),
    onSuccess: () => invalidate(qc),
    onError: () => ShowToast({ type: "error", message: "Failed to add user" }),
  });
};

export const useRemoveChargeAuth = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.chargeAuth.remove(id),
    onSuccess: () => invalidate(qc),
    onError: () =>
      ShowToast({ type: "error", message: "Failed to remove user" }),
  });
};

export const useResetChargeAuth = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.chargeAuth.reset(id),
    onSuccess: () => {
      ShowToast({ type: "success", message: "Authenticator reset" });
      invalidate(qc);
    },
    onError: () =>
      ShowToast({ type: "error", message: "Failed to reset authenticator" }),
  });
};
